import { useCallback, useEffect, useState } from 'react';
import {
  FlaskConical, Plus, Trash2, ExternalLink, XCircle,
  AlertTriangle, CheckCircle2, Info, Loader2,
} from 'lucide-react';
import Button from '../ui/Button';
import sprayService from '../../services/spray';
import { toNum } from '@/utils/numeric';
import type {
  ChemicalDetail, ChemicalIncompatibility, CompatibilitySeverity, IncompatibilityInput,
} from '../../types/spray';

interface ChemicalCompatibilityCardProps {
  chemical: ChemicalDetail;
  /** The signed-in user may edit the shared chemical registry. */
  canEdit: boolean;
  onSaved?: () => void;
}

const SEVERITY_META: Record<CompatibilitySeverity, { label: string; tone: string; Icon: typeof XCircle }> = {
  incompatible: { label: 'Do not mix', tone: 'text-red-700 bg-red-50 border-red-200',       Icon: XCircle },
  caution:      { label: 'Caution',    tone: 'text-amber-700 bg-amber-50 border-amber-200', Icon: AlertTriangle },
  compatible:   { label: 'Compatible', tone: 'text-green-700 bg-green-50 border-green-200', Icon: CheckCircle2 },
};

/** How a rule names its partner, for display. */
function partnerLabel(r: ChemicalIncompatibility): string {
  if (r.other_product_name) return r.other_product_name;
  if (r.other_active_ingredient) return `anything containing ${r.other_active_ingredient}`;
  if (r.other_moa_group) return `mode-of-action group ${r.other_moa_group}`;
  if (r.other_formulation_type) return `${r.other_formulation_type} formulations`;
  return 'an unspecified partner';
}

// ============================================================================
// Add-rule form
// ============================================================================

type PartnerKind = 'active_ingredient' | 'moa_group' | 'formulation';

function AddRuleForm({
  chemicalId,
  onSaved,
  onCancel,
}: {
  chemicalId: number;
  onSaved: () => void;
  onCancel: () => void;
}) {
  // Active ingredient is the default because it is what labels actually name.
  // A COMPATIBILITY section warns about copper or about alkaline products; it
  // almost never names a competitor's trade name.
  const [kind, setKind]         = useState<PartnerKind>('active_ingredient');
  const [partner, setPartner]   = useState('');
  const [severity, setSeverity] = useState<CompatibilitySeverity>('incompatible');
  const [reason, setReason]     = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [scope, setScope]       = useState<'global' | 'organisation'>('global');
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState<string | null>(null);

  const save = async () => {
    if (!partner.trim()) {
      setError('Name what this product must not be mixed with.');
      return;
    }
    setSaving(true);
    setError(null);

    const payload: IncompatibilityInput = { severity, scope };
    if (kind === 'active_ingredient') payload.other_active_ingredient = partner.trim();
    if (kind === 'moa_group')         payload.other_moa_group = partner.trim();
    if (kind === 'formulation')       payload.other_formulation_type = partner.trim();
    if (reason.trim())                payload.reason = reason.trim();
    if (sourceUrl.trim())             payload.source_url = sourceUrl.trim();

    const res = await sprayService.createIncompatibility(chemicalId, payload);
    setSaving(false);
    if (res.success) onSaved();
    else setError((res as { error?: { message?: string } }).error?.message || 'Could not save the rule');
  };

  return (
    <div className="rounded-lg border border-dashed border-neutral-300 p-3 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-neutral-600 mb-1">Applies to</label>
          <select
            value={kind}
            onChange={e => setKind(e.target.value as PartnerKind)}
            className="w-full border border-neutral-300 rounded px-2 py-2 text-sm"
          >
            <option value="active_ingredient">An active ingredient</option>
            <option value="moa_group">A mode-of-action group</option>
            <option value="formulation">A formulation type</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-neutral-600 mb-1">
            {kind === 'active_ingredient' ? 'Active ingredient'
              : kind === 'moa_group' ? 'Group code'
              : 'Formulation code'}
          </label>
          <input
            type="text"
            value={partner}
            onChange={e => setPartner(e.target.value)}
            placeholder={kind === 'active_ingredient' ? 'e.g. copper hydroxide'
              : kind === 'moa_group' ? 'e.g. 3A' : 'e.g. EC'}
            className="w-full border border-neutral-300 rounded px-2 py-2 text-sm"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-neutral-600 mb-1">Severity</label>
          <select
            value={severity}
            onChange={e => setSeverity(e.target.value as CompatibilitySeverity)}
            className="w-full border border-neutral-300 rounded px-2 py-2 text-sm"
          >
            <option value="incompatible">Do not mix</option>
            <option value="caution">Caution — jar test first</option>
            <option value="compatible">Compatible (label states so)</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-neutral-600 mb-1">Source of this rule</label>
          <select
            value={scope}
            onChange={e => setScope(e.target.value as 'global' | 'organisation')}
            className="w-full border border-neutral-300 rounded px-2 py-2 text-sm"
          >
            <option value="global">From the label — share with everyone</option>
            <option value="organisation">Our own experience — keep private</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-neutral-600 mb-1">Reason</label>
        <input
          type="text"
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="e.g. Forms an insoluble precipitate that blocks nozzles"
          className="w-full border border-neutral-300 rounded px-2 py-2 text-sm"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-neutral-600 mb-1">Source URL (label or SDS)</label>
        <input
          type="url"
          value={sourceUrl}
          onChange={e => setSourceUrl(e.target.value)}
          placeholder="https://…"
          className="w-full border border-neutral-300 rounded px-2 py-2 text-sm"
        />
      </div>

      {error && <p className="text-xs text-red-600">{error}</p>}

      <div className="flex items-center gap-2">
        <Button variant="primary" size="sm" onClick={save} disabled={saving}>
          {saving && <Loader2 className="h-4 w-4 animate-spin" />}
          Save rule
        </Button>
        <button type="button" onClick={onCancel} className="text-xs text-neutral-500 hover:text-neutral-700">
          Cancel
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Card
// ============================================================================

/**
 * Tank-mix compatibility for one product: its own mixing properties, and the
 * rules recorded about what it must not be mixed with.
 *
 * Global rules come from labels and are shared across organisations, the same
 * way the chemical registry itself is. Org-scoped rules are one farm's own
 * experience and stay private to them — both are shown here, labelled, because
 * an operator needs to know which kind of claim they are reading.
 */
function ChemicalCompatibilityCard({ chemical, canEdit, onSaved }: ChemicalCompatibilityCardProps) {
  const [rules, setRules]   = useState<ChemicalIncompatibility[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    const res = await sprayService.getIncompatibilities(chemical.id);
    if (res.success && res.data) setRules(res.data);
    setLoading(false);
  }, [chemical.id]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id: number) => {
    const res = await sprayService.deleteIncompatibility(id);
    if (res.success) load();
  };

  const phMin = toNum(chemical.ph_min);
  const phMax = toNum(chemical.ph_max);
  const hasProps = chemical.mix_order != null || phMin != null || phMax != null
    || chemical.max_water_hardness_ppm != null || chemical.requires_jar_test
    || !!chemical.compatibility_notes;

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide flex items-center gap-2">
          <FlaskConical className="h-4 w-4 text-primary-600" />
          Tank-mix compatibility
        </h3>
        {canEdit && !adding && (
          <Button variant="outline" size="sm" onClick={() => setAdding(true)}>
            <Plus className="h-4 w-4" />
            Add rule
          </Button>
        )}
      </div>

      {chemical.requires_jar_test && (
        <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2">
          <p className="text-sm font-medium text-amber-800 flex items-center gap-1.5">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            Label requires a jar test before any tank mix
          </p>
        </div>
      )}

      {hasProps && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          {chemical.mix_order != null && (
            <div>
              <p className="text-neutral-400">Fill order</p>
              <p className="font-medium text-neutral-800">Position {chemical.mix_order}</p>
            </div>
          )}
          {(phMin != null || phMax != null) && (
            <div>
              <p className="text-neutral-400">Spray-water pH</p>
              <p className="font-medium text-neutral-800">
                {phMin ?? '—'} – {phMax ?? '—'}
              </p>
            </div>
          )}
          {chemical.max_water_hardness_ppm != null && (
            <div>
              <p className="text-neutral-400">Max water hardness</p>
              <p className="font-medium text-neutral-800">{chemical.max_water_hardness_ppm} ppm</p>
            </div>
          )}
        </div>
      )}

      {chemical.compatibility_notes && (
        <div className="text-xs">
          <p className="text-neutral-400 mb-0.5">From the label</p>
          <p className="text-neutral-700 whitespace-pre-line">{chemical.compatibility_notes}</p>
          {chemical.compatibility_source_url && (
            <a
              href={chemical.compatibility_source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-0.5 text-primary-600 hover:underline mt-1"
            >
              Source <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      )}

      {adding && (
        <AddRuleForm
          chemicalId={chemical.id}
          onSaved={() => { setAdding(false); load(); onSaved?.(); }}
          onCancel={() => setAdding(false)}
        />
      )}

      {loading ? (
        <p className="text-sm text-neutral-400">Loading rules…</p>
      ) : rules.length === 0 ? (
        <p className="text-sm text-neutral-500 flex items-start gap-1.5">
          <Info className="h-4 w-4 text-neutral-400 shrink-0 mt-0.5" />
          <span>
            No mixing rules recorded for this product. That means none have been entered — it is
            not a statement that this product mixes with everything. Transcribe the label&rsquo;s
            COMPATIBILITY section here and every spray plan using it gets checked automatically.
          </span>
        </p>
      ) : (
        <div className="space-y-2">
          {rules.map(r => {
            const meta = SEVERITY_META[r.severity];
            return (
              <div key={r.id} className={`rounded border px-3 py-2 ${meta.tone}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium flex items-start gap-1.5">
                      <meta.Icon className="h-4 w-4 shrink-0 mt-0.5" />
                      <span>{meta.label}: {partnerLabel(r)}</span>
                    </p>
                    {r.reason && <p className="text-xs mt-1 opacity-90">{r.reason}</p>}
                    <div className="flex flex-wrap items-center gap-x-3 mt-1 text-xs opacity-75">
                      <span>{r.organisation_id ? 'Your organisation' : 'From the label'}</span>
                      {r.source_url && (
                        <a
                          href={r.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-0.5 underline"
                        >
                          Source <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  </div>
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => remove(r.id)}
                      className="opacity-60 hover:opacity-100 shrink-0"
                      aria-label="Remove this rule"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ChemicalCompatibilityCard;
